import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Select } from "@/components/ui";
import { regionDisplayName } from "@/lib/regionLabel";
import { regionKeys, regionService } from "@/services/regionService";

export interface RegionSelectProps {
  value: string;
  onChange: (regionId: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

export function RegionSelect({
  value,
  onChange,
  label,
  placeholder,
  error,
  disabled = false,
  required = false,
}: RegionSelectProps) {
  const { t } = useTranslation("common");

  const { data: regions = [], isLoading } = useQuery({
    queryKey: regionKeys.active(),
    queryFn: () => regionService.listActive(),
  });

  const options = useMemo(
    () =>
      regions.map((region) => {
        const name = regionDisplayName(region.code, t, region.name);
        return {
          value: region.id,
          label: `${name} (${region.code})`,
        };
      }),
    [regions, t],
  );

  useEffect(() => {
    if (!value && regions.length === 1) {
      onChange(regions[0].id);
    }
  }, [regions, value, onChange]);

  const resolvedError =
    error ??
    (regions.length === 0 && !isLoading
      ? t("region.empty")
      : undefined);

  return (
    <Select
      label={label ?? t("region.label")}
      placeholder={placeholder}
      options={options}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={resolvedError}
      disabled={disabled || isLoading || regions.length === 0}
      required={required}
    />
  );
}
